import { useState } from "react";
import { Sparkle, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const QUESTIONS = [
  { key: "name", q: "Who was this person?", hint: "Their full name", type: "input" },
  { key: "verification_relationship", q: "How did you know them?", hint: "e.g. classmate, student, colleague, family", type: "input" },
  { key: "institution", q: "Which school, college or university did they attend or teach at?", hint: "Institution name", type: "input" },
  { key: "class_batch", q: "What class or batch were they in?", hint: "e.g. Class of 1985", type: "input" },
  { key: "profession", q: "What did they do in life?", hint: "Profession or main role (optional)", type: "input" },
  { key: "memory", q: "What do you remember most about them?", hint: "A story, a moment, their character", type: "textarea" },
  { key: "achievements", q: "What did they accomplish?", hint: "Achievements, contributions (optional)", type: "textarea" },
  { key: "contributions", q: "What would you like future generations to know?", hint: "Anything else worth preserving (optional)", type: "textarea" },
];

export default function MemorialAssistant({ onApply }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const cur = QUESTIONS[step];
  const last = step === QUESTIONS.length - 1;
  const set = (v) => setAns({ ...ans, [cur.key]: v });

  const next = () => { if (!last) setStep(step + 1); else finish(); };
  const finish = () => {
    onApply(ans);
    setOpen(false);
    setStep(0);
    setAns({});
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setStep(0); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full gap-1.5" data-testid="assistant-open-btn"><Sparkle size={15} weight="fill" className="text-violet-600" /> Guided AI Assistant</Button>
      </DialogTrigger>
      <DialogContent data-testid="assistant-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-violet-600" /> Memorial Assistant</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-1.5">
            {QUESTIONS.map((_, i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-violet-500" : "bg-muted"}`} />
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Question {step + 1} of {QUESTIONS.length}</p>
          <div className="space-y-2">
            <p className="font-heading text-lg font-medium">{cur.q}</p>
            <p className="text-xs text-muted-foreground">{cur.hint}</p>
            {cur.type === "textarea"
              ? <Textarea value={ans[cur.key] || ""} onChange={(e) => set(e.target.value)} className="min-h-[100px]" data-testid="assistant-answer" autoFocus />
              : <Input value={ans[cur.key] || ""} onChange={(e) => set(e.target.value)} data-testid="assistant-answer" autoFocus onKeyDown={(e) => { if (e.key === "Enter") next(); }} />}
          </div>
          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" size="sm" className="rounded-full gap-1" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="assistant-back"><ArrowLeft size={14} /> Back</Button>
            <Button size="sm" className="rounded-full gap-1" onClick={next} data-testid="assistant-next">
              {last ? "Fill the form" : "Next"} <ArrowRight size={14} />
            </Button>
          </div>
          <p className="rounded-lg bg-muted/40 p-2 text-[11px] text-muted-foreground">Your answers fill the memorial form — you can then generate an AI draft and review everything before publishing. Nothing is invented.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
